import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, ArrowRight, Facebook, Instagram, Linkedin, Twitter, Youtube, Music2, Globe } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import api from '@/lib/api';
import { useLang } from '@/contexts/LangContext';

const asArray = (value) => (Array.isArray(value) ? value : []);
const asObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

export const Footer = () => {
  const { t, lang } = useLang();
  const [footer, setFooter] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [courses, setCourses] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const quickLinkKeyByHref = {
    '/about': 'nav.about',
    '/placement-test': 'nav.placement_test',
    '/contact': 'nav.contact',
    '/login': 'auth.sign_in',
    '/register': 'nav.register',
    '/courses': 'nav.courses',
    '/corporate': 'nav.corporate',
  };

  const getQuickLinkLabel = (link) => {
    const key = quickLinkKeyByHref[link.href];
    return key ? t(key) : link.label;
  };

  const baseQuickLinks = [
    { label: t('nav.about'), href: '/about' },
    { label: t('nav.placement_test'), href: '/placement-test' },
    { label: t('nav.corporate'), href: '/corporate' },
    { label: t('nav.contact'), href: '/contact' },
    { label: t('nav.register'), href: '/register' },
  ];

  const footerQuickLinks = asArray(footer?.quick_links);
  const quickLinks = [...footerQuickLinks, ...baseQuickLinks]
    .filter((item) => item?.href)
    .filter((item, index, arr) => arr.findIndex((entry) => entry.href === item.href) === index);
  const visibleCourses = courses.filter((course) => course?.id && course?.slug && course?.title);
  const visibleSocialLinks = socialLinks.filter((link) => link?.id && (link.post_url || link.url));

  useEffect(() => {
    Promise.all([
      api.get('/content/footer').catch(() => ({ data: {} })),
      api.get('/content/contact-info').catch(() => ({ data: {} })),
      api.get('/courses', { params: { audience: 'individual' } }).catch(() => ({ data: [] })),
      api.get('/social-links').catch(() => ({ data: [] })),
    ]).then(([fRes, cRes, coRes, sRes]) => {
      setFooter(asObject(fRes.data));
      setContactInfo(asObject(cRes.data));
      setCourses(asArray(coRes.data).slice(0, 6));
      setSocialLinks(asArray(sRes.data));
    });
  }, [lang]);

  return (
    <footer data-testid="main-footer" className="bf-site-footer relative overflow-hidden text-white">
      <div className="pointer-events-none absolute inset-0 opacity-95">
        <div className="bf-footer-orb -left-24 top-0 h-80 w-80 bg-[#6c8bc6]/24" />
        <div className="bf-footer-orb bottom-0 right-0 h-96 w-96 bg-white/9" />
        <div className="bf-footer-gridline" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/24 to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="py-14 sm:py-16 lg:py-20">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-[1.18fr_0.86fr_0.86fr_1.1fr] lg:gap-6">
            {/* Brand */}
            <div className="bf-footer-panel bf-footer-brand-panel">
              <Link to="/" className="inline-flex items-center gap-2.5 rounded-2xl outline-none transition-transform duration-300 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-white/40">
                <img src="/befluent-logo-white.png" alt={t('brand.alt')} className="h-11 w-auto drop-shadow-sm" />
              </Link>
              <p className="mt-5 max-w-sm text-sm leading-7 text-white/70">
                {footer?.brand_description || t('footer.brand_description')}
              </p>
            </div>

            {/* Courses from API */}
            <div className="bf-footer-panel">
              <h4 className="bf-footer-heading">{t('footer.courses')}</h4>
              <ul className="space-y-3">
                {visibleCourses.map((c) => (
                  <li key={c.id}>
                    <Link to={`/courses/${c.slug}`} className="bf-footer-link group gap-2 text-sm">
                      <ArrowRight className="bf-footer-link-icon h-3.5 w-3.5" />
                      {c.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links from API */}
            <div className="bf-footer-panel">
              <h4 className="bf-footer-heading">{t('footer.quick_links')}</h4>
              <ul className="space-y-3">
                {quickLinks.map((l) => (
                  <li key={l.href}>
                    <Link to={l.href} className="bf-footer-link group gap-2 text-sm">
                      <ArrowRight className="bf-footer-link-icon h-3.5 w-3.5" />
                      {getQuickLinkLabel(l)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact from API */}
            <div className="bf-footer-panel bf-footer-contact-panel">
              <h4 className="bf-footer-heading">{t('footer.contact')}</h4>
              <ul className="space-y-3.5">
                {contactInfo?.address && (
                  <li className="bf-footer-contact-item">
                    <span className="bf-footer-contact-icon">
                      <MapPin className="h-4 w-4" />
                    </span>
                    <span className="whitespace-pre-line leading-6">{contactInfo.address}</span>
                  </li>
                )}
                {contactInfo?.phone && (
                  <li className="bf-footer-contact-item">
                    <span className="bf-footer-contact-icon">
                      <Phone className="h-4 w-4" />
                    </span>
                    <a href={`tel:${contactInfo.phone}`} className="transition-colors hover:text-white">{contactInfo.phone}</a>
                  </li>
                )}
                {contactInfo?.email && (
                  <li className="bf-footer-contact-item">
                    <span className="bf-footer-contact-icon">
                      <Mail className="h-4 w-4" />
                    </span>
                    <a href={`mailto:${contactInfo.email}`} className="transition-colors hover:text-white">{contactInfo.email}</a>
                  </li>
                )}
                {contactInfo?.hours && (
                  <li className="bf-footer-contact-item">
                    <span className="bf-footer-contact-icon">
                      <Clock className="h-4 w-4" />
                    </span>
                    <span className="whitespace-pre-line leading-6">{contactInfo.hours}</span>
                  </li>
                )}
              </ul>
              {/* Social Media Links */}
              {visibleSocialLinks.length > 0 && (
                <div className="bf-social-row mt-5 flex items-center gap-2.5">
                  {visibleSocialLinks.map((link) => {
                    const iconMap = { Facebook, Instagram, Linkedin, Twitter, Youtube, Music2 };
                    const Icon = iconMap[link.icon] || Globe;
                    const href = link.post_url || link.url;
                    return (
                      <a key={link.id} href={href} target="_blank" rel="noopener noreferrer" className="bf-social-link" title={link.name} aria-label={link.name}>
                        <Icon className="h-4 w-4" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <Separator className="bg-white/10" />
        <div className="flex flex-col items-center justify-between gap-4 py-6 sm:flex-row">
          <p className="text-xs text-white/48">&copy; {new Date().getFullYear()} {t('brand.full_name')}. {t('footer.rights')}</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/legal/privacy-policy" className="bf-footer-bottom-link">{t('footer.privacy')}</Link>
            <Link to="/legal/terms-and-conditions" className="bf-footer-bottom-link">{t('footer.terms')}</Link>
            <span className="bf-hexolution-credit" aria-label="Powered by HEXolution">
              <span>Powered by</span>
              <a href="https://hex-solution.com" target="_blank" rel="noopener noreferrer">HEXolution</a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
